// Print-friendly account history page.
// Opens a new tab with a styled serif layout, auto-triggers the browser print
// dialog on load. Serves the current filtered + sorted rows (no pagination).

import type { BankTransaction } from "./types";
import type { ColumnLayout } from "./constants";

export interface PrintRow extends BankTransaction {
  runningBalance: number;
}

export interface PrintMeta {
  companyName: string;
  companyAddress?: string;
  accountName: string;
  accountId: string;
  accountType: string;
  layout: ColumnLayout;
}

function fmtDateDMY(iso: string): string {
  const parts = iso.split("-");
  if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
  return iso;
}

function fmtAmount(n: number | undefined): string {
  if (n == null) return "";
  const abs = Math.abs(n);
  return (n < 0 ? "-" : "") + "$" + abs.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtBalance(n: number): string {
  const abs = Math.abs(n);
  return (n < 0 ? "-$" : "$") + abs.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function labelForType(type: string): string {
  const map: Record<string, string> = {
    payment: "Payment",
    cheque: "Cheque",
    deposit: "Deposit",
    transfer: "Transfer",
    debit: "Debit",
    payroll: "Payroll",
    interest: "Interest",
    "bank-fee": "Bank fee",
  };
  return map[type] ?? type;
}

function escapeHtml(s: string | number | undefined | null): string {
  if (s == null) return "";
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function todayDMY(): string {
  const now = new Date();
  const dd = String(now.getDate()).padStart(2, "0");
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  return `${dd}/${mm}/${now.getFullYear()}`;
}

function buildHtml(rows: PrintRow[], meta: PrintMeta): string {
  const dates = rows.map((r) => r.date).sort();
  const first = dates[0] ? fmtDateDMY(dates[0]) : "";
  const last = dates[dates.length - 1] ? fmtDateDMY(dates[dates.length - 1]) : "";
  const today = todayDMY();

  const rowsHtml = rows.map((r) => {
    const leftVal = r[meta.layout.leftFieldKey];
    const rightVal = r[meta.layout.rightFieldKey];
    const check = r.reconcileStatus === "reconciled" ? "R" : r.reconcileStatus === "cleared" ? "C" : "";
    return `
      <tr>
        <td>${escapeHtml(fmtDateDMY(r.date))}</td>
        <td>${escapeHtml(r.refNo)}<span class="sub">${escapeHtml(labelForType(r.type))}</span></td>
        <td>${escapeHtml(r.payee)}<span class="sub">${escapeHtml(r.linkedAccountName)}</span></td>
        <td>${escapeHtml(r.memo)}</td>
        <td class="right">${escapeHtml(fmtAmount(leftVal))}</td>
        <td class="right">${escapeHtml(fmtAmount(rightVal))}</td>
        <td class="center">${escapeHtml(check)}</td>
        <td class="right">${escapeHtml(fmtBalance(r.runningBalance))}</td>
      </tr>
    `;
  }).join("");

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(meta.accountName)} account history ${today}</title>
  <style>
    * { box-sizing: border-box; }
    body { font-family: Georgia, 'Times New Roman', serif; margin: 0; padding: 0; color: #000; background: #f5f5f5; }
    .sheet { max-width: 850px; margin: 24px auto; background: #fff; box-shadow: 0 4px 16px rgba(0,0,0,0.1); padding: 48px 56px; }
    .page-header { text-align: center; margin-bottom: 24px; }
    .company-name { font-size: 20px; font-weight: bold; letter-spacing: 0.2px; }
    .company-address { font-size: 11px; color: #333; margin-top: 4px; }
    .report-title { font-size: 15px; font-weight: bold; margin-top: 24px; }
    .report-period { font-size: 11px; color: #333; margin-top: 4px; }
    table { width: 100%; border-collapse: collapse; margin-top: 20px; font-size: 10.5px; }
    th { background: #fff; text-align: left; padding: 6px 8px; font-weight: bold; border-bottom: 1.5px solid #000; border-top: 1.5px solid #000; font-size: 10px; }
    th.right { text-align: right; }
    th.center { text-align: center; }
    td { padding: 6px 8px; border-bottom: 0.5px solid #ccc; vertical-align: top; }
    td.right { text-align: right; font-variant-numeric: tabular-nums; }
    td.center { text-align: center; }
    td .sub { font-size: 9.5px; color: #666; display: block; margin-top: 2px; }
    .page-footer { max-width: 850px; margin: 16px auto 24px; text-align: center; font-size: 9.5px; color: #666; }
    @page { margin: 0.6in; size: letter; }
    @media print {
      body { background: #fff; }
      .sheet { max-width: none; margin: 0; padding: 0; box-shadow: none; }
      .page-footer { margin: 24px 0 0; }
    }
  </style>
</head>
<body>
  <div class="sheet">
    <div class="page-header">
      <div class="company-name">${escapeHtml(meta.companyName)}</div>
      ${meta.companyAddress ? `<div class="company-address">${escapeHtml(meta.companyAddress)}</div>` : ""}
      <div class="report-title">Account history: ${escapeHtml(meta.accountName)}</div>
      <div class="report-period">Account ${escapeHtml(meta.accountId)} &middot; ${escapeHtml(meta.accountType)} &middot; ${first} to ${last}</div>
    </div>
    <table>
      <thead>
        <tr>
          <th>Date</th>
          <th>Ref No / Type</th>
          <th>Payee / Account</th>
          <th>Memo</th>
          <th class="right">${escapeHtml(meta.layout.leftLabel)}</th>
          <th class="right">${escapeHtml(meta.layout.rightLabel)}</th>
          <th class="center">&#10003;</th>
          <th class="right">${escapeHtml(meta.layout.balanceLabel)}</th>
        </tr>
      </thead>
      <tbody>${rowsHtml}</tbody>
    </table>
  </div>
  <div class="page-footer">Generated ${today} &middot; Novala</div>
  <script>
    window.addEventListener('load', function() {
      setTimeout(function() { window.print(); }, 250);
    });
  </script>
</body>
</html>`;
}

export function openPrintPreview(rows: PrintRow[], meta: PrintMeta): void {
  const html = buildHtml(rows, meta);
  const w = window.open("", "_blank");
  if (!w) {
    alert("Please allow pop-ups for this site to print the account history.");
    return;
  }
  w.document.open();
  w.document.write(html);
  w.document.close();
}